import React from "react";
import { apiListBom, apiCreateBom, apiUpdateBom, apiListBahan, apiListPos, formatApiErrorDetail } from "@/lib/api";
import { qty } from "@/lib/format";
import { Plus, Power, X } from "lucide-react";

// Setting Bahan (BOM) of one barang (F1-E). Owner only; the server validates
// everything (bahan + koefisien required, PER_M2 only for barang per m²).
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const lbl = "text-[13px] font-semibold text-slate-200";

// Example usage in words, e.g. "2 × 1 m, qty 1 → 2,2 M2 FLEKSI280GR".
function contoh(line) {
  const k = line.koefisien_milli;
  const pakai = (m) => `${qty(m)} ${line.bahan.satuan || ""} ${line.bahan.kode}`;
  return line.mode_pakai === "PER_M2" ? `2 × 1 m, qty 1 → ${pakai(2 * k)}` : `qty 1 → ${pakai(k)}`;
}

export function BomPanel({ barang, onClose, lihatSaja = false }) {  // lihatSaja: Manager (owner 2026-10-12)
  const [data, setData] = React.useState(null);
  const [bahan, setBahan] = React.useState([]);
  const [f, setF] = React.useState({ bahan_id: "", mode_pakai: "PER_QTY", koefisien: "", pos_pemakaian: "" });
  const [posList, setPosList] = React.useState([]);
  const [edit, setEdit] = React.useState({});
  const [error, setError] = React.useState(null);
  const perM2 = barang.mode_harga === "PER_M2";

  const load = React.useCallback(() => apiListBom(barang.id).then(setData).catch((e) => setError(errText(e))), [barang.id]);
  React.useEffect(() => {
    load(); apiListBahan({ aktif: true }).then(setBahan).catch(() => {});
    apiListPos().then(setPosList).catch(() => {});
  }, [load]);
  const posNama = (c) => posList.find((p) => p.code === c)?.nama || c;
  // owner 2026-10-08: a line without its station is not taken again when a QC rework repeats the stations
  const tanpaPos = (data?.items || []).filter((l) => l.is_active && !l.pos_pemakaian);

  const tambah = async (e) => {
    e.preventDefault(); setError(null);
    try { await apiCreateBom({ barang_id: barang.id, ...f, pos_pemakaian: f.pos_pemakaian || null }); setF({ bahan_id: "", mode_pakai: f.mode_pakai, koefisien: "", pos_pemakaian: f.pos_pemakaian }); load(); }
    catch (e2) { setError(errText(e2)); }
  };
  const ubah = async (line, patch) => {
    setError(null);
    try { await apiUpdateBom(line.id, patch); setEdit({}); load(); } catch (e) { setError(errText(e)); }
  };

  return (
    <div data-testid={`bom-panel-${barang.kode}`} className="rounded-xl border border-cyan-500/30 bg-slate-900/80 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">Setting Bahan (BOM) — <span className="font-mono text-amber-300">{barang.kode}</span> {barang.nama}</h2>
          <p className="text-xs text-slate-500">{perM2 ? "Barang per m²: PER_M2 = koefisien × P × L × qty; PER_QTY = koefisien × qty." : barang.mode_harga === "PER_METER_LARI" ? "Barang per meter lari: koefisien × panjang SEBENARNYA (m), bukan panjang ditagih." : "Barang per qty: koefisien × qty."} Stok bahan dipotong saat nota terbit. Perubahan hanya berlaku untuk nota baru.</p>
        </div>
        <button data-testid="bom-close" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={16} /></button>
      </div>
      {error && <p data-testid="bom-error" className="text-xs text-red-300">{error}</p>}
      {tanpaPos.length > 0 && <p data-testid="bom-tanpa-pos" className="rounded border border-amber-500/40 border-l-4 border-l-amber-500 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">{tanpaPos.length} baris belum punya <b>Pos pemakaian</b>: bahan itu <b>tidak terpotong</b> saat rework QC. Pilih pos di kolom Pos.</p>}
      <table className="w-full text-sm" data-testid="bom-table">
        <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
          <th className="py-2">Bahan</th><th className="py-2">Pos pemakaian</th><th className="py-2">Mode</th><th className="py-2 text-right">Koefisien</th><th className="py-2 pl-4">Contoh</th><th className="py-2">Status</th><th /></tr></thead>
        <tbody>
          {(data?.items || []).map((l) => (
            <tr key={l.id} data-testid={`bom-row-${l.bahan.kode}`} className={`border-b border-slate-800/60 ${l.is_active ? "" : "text-slate-500"}`}>
              <td className="py-2"><span className="font-mono text-amber-300">{l.bahan.kode}</span> <span className="text-xs">{l.bahan.nama} ({l.bahan.satuan})</span></td>
              <td className="py-2 text-xs">
                <select data-testid={`bom-pos-${l.bahan.kode}`} value={l.pos_pemakaian || ""} disabled={!l.is_active || lihatSaja} onChange={(e) => ubah(l, { pos_pemakaian: e.target.value })}
                  className={`rounded border bg-slate-950 px-1.5 py-1 ${l.is_active && !l.pos_pemakaian ? "border-amber-500/60 text-amber-300" : "border-slate-700 text-slate-200"}`}>
                  <option value="">— belum diisi —</option>
                  {posList.map((p) => <option key={p.code} value={p.code}>{p.nama || p.code}</option>)}
                </select>
                {l.pos_pemakaian && !posList.length ? <span className="ml-1">{posNama(l.pos_pemakaian)}</span> : null}
              </td>
              <td className="py-2 text-xs">{l.mode_pakai === "PER_M2" ? "per m²" : "per qty"}</td>
              <td className="py-2 text-right font-mono">
                {edit.id === l.id ? (
                  <input data-testid="bom-edit-koef" type="number" step="0.001" min="0.001" value={edit.koefisien} onChange={(e) => setEdit({ ...edit, koefisien: e.target.value })} className={`${inputCls} mt-0 w-24 text-right`} />
                ) : l.koefisien}
              </td>
              <td className="py-2 pl-4 text-xs text-slate-400">{contoh(l)}</td>
              <td className="py-2 text-xs">{l.is_active ? "Aktif" : "Nonaktif"}</td>
              <td className="py-2 text-right whitespace-nowrap space-x-1">
                {lihatSaja ? null : edit.id === l.id ? (<>
                  <button data-testid="bom-edit-save" onClick={() => ubah(l, { koefisien: edit.koefisien })} className="rounded border border-amber-500/40 px-2 py-1 text-xs text-amber-300">Simpan</button>
                  <button onClick={() => setEdit({})} className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-400">Batal</button>
                </>) : (<>
                  {l.is_active && <button data-testid={`bom-edit-${l.bahan.kode}`} onClick={() => setEdit({ id: l.id, koefisien: l.koefisien.replace(",", ".") })} className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300">Ubah</button>}
                  <button data-testid={`bom-toggle-${l.bahan.kode}`} onClick={() => ubah(l, { is_active: !l.is_active })} className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300"><Power size={11} />{l.is_active ? "Nonaktifkan" : "Aktifkan"}</button>
                </>)}
              </td>
            </tr>
          ))}
          {data && data.items.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-xs text-amber-300">Belum ada BOM: barang ini tetap bisa dijual, tetapi stok bahan tidak terpotong.</td></tr>}
        </tbody>
      </table>
      {!lihatSaja && <form data-testid="bom-form" onSubmit={tambah} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div className="col-span-2"><label className={lbl}>Bahan</label>
          <select data-testid="bom-bahan" value={f.bahan_id} onChange={(e) => setF({ ...f, bahan_id: e.target.value })} required className={inputCls}>
            <option value="">— pilih bahan —</option>
            {bahan.map((b) => <option key={b.id} value={b.id}>{b.kode} — {b.nama} ({b.satuan})</option>)}
          </select></div>
        <div><label className={lbl}>Mode pakai</label>
          <select data-testid="bom-mode" value={f.mode_pakai} onChange={(e) => setF({ ...f, mode_pakai: e.target.value })} className={inputCls}>
            <option value="PER_QTY">per qty</option>{perM2 && <option value="PER_M2">per m² (P × L)</option>}
          </select></div>
        <div><label className={lbl}>Pos pemakaian</label>
          <select data-testid="bom-pos" value={f.pos_pemakaian} onChange={(e) => setF({ ...f, pos_pemakaian: e.target.value })} className={inputCls}>
            <option value="">— belum diisi —</option>
            {posList.map((p) => <option key={p.code} value={p.code}>{p.nama || p.code}</option>)}
          </select></div>
        <div><label className={lbl}>Koefisien</label>
          <input data-testid="bom-koef" type="number" step="0.001" min="0.001" value={f.koefisien} onChange={(e) => setF({ ...f, koefisien: e.target.value })} required className={inputCls} /></div>
        <button data-testid="bom-submit" type="submit" className="inline-flex items-center justify-center gap-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950"><Plus size={14} /> Tambah bahan</button>
      </form>}
    </div>
  );
}
